<!--
  GlobalDialog - 전역 다이얼로그 알림창 컴포넌트
  
  사용법:
  ======
  
  1. 기본 사용 (Composable 사용)
  --------------------------------
  import { useDialog } from '@/shared/composables/useDialog';
  
  const { confirm, alert, success, error, warning, info, deleteConfirm } = useDialog();
  
  2. 확인 다이얼로그 (Confirm)
  --------------------------------
  // 간단한 확인
  const result = await confirm('정말 삭제하시겠습니까?');
  if (result) {
    // 확인 클릭 시 처리
  }
  
  // 옵션 사용
  const result = await confirm({
    title: '삭제 확인',
    message: '이 항목을 삭제하시겠습니까?',
    type: 'warning',
    confirmText: '삭제',
    cancelText: '취소',
    timeout: 5000  // 5초 후 자동 닫힘 (선택)
  });
  
  // 삭제 확인 (간편)
  const result = await deleteConfirm('이 항목을 삭제하시겠습니까?');
  
  3. 알림 다이얼로그 (Alert)
  --------------------------------
  // 기본 알림
  await alert('처리가 완료되었습니다');
  
  // 옵션 사용 (타임아웃 설정 가능)
  await alert({
    title: '알림',
    message: '처리가 완료되었습니다.',
    type: 'success',
    timeout: 3000  // 3초 후 자동 닫힘
  });
  
  4. 간편 메서드
  --------------------------------
  await success('저장되었습니다');
  await success('저장되었습니다', '성공');
  
  await error('오류가 발생했습니다');
  await error('저장에 실패했습니다', '오류');
  
  await warning('주의가 필요합니다');
  await info('알림 정보입니다');
  
  5. 타임아웃 기능
  --------------------------------
  // 타임아웃 설정 (밀리초)
  await alert({
    title: '알림',
    message: '3초 후 자동으로 닫힙니다.',
    timeout: 3000  // 3초 후 자동 닫힘
  });
  
  // 타임아웃 없음 (기본값)
  await alert({
    title: '알림',
    message: '수동으로 닫아야 합니다.',
    timeout: 0  // 또는 timeout 설정 안 함
  });
  
  6. Persistent Dialog (외부 클릭 방지)
  --------------------------------
  await confirm({
    title: '중요한 확인',
    message: '이 작업은 되돌릴 수 없습니다.',
    persistent: true  // 외부 클릭 방지
  });
  
  옵션:
  - title: 제목 (필수)
  - message: 메시지 (필수)
  - type: 'success' | 'error' | 'warning' | 'info' | 'question' (기본: 'info')
  - confirmText: 확인 버튼 텍스트 (기본: '확인')
  - cancelText: 취소 버튼 텍스트 (빈 문자열이면 취소 버튼 숨김)
  - persistent: 외부 클릭 방지 여부 (기본: false)
  - width: 다이얼로그 너비 (기본: '600px')
  - timeout: 자동 닫힘 시간 (밀리초, 0이면 자동 닫힘 없음, 기본: 0)
-->

<script setup lang="ts">
import { useDialogStore } from '@/shared/stores/dialog';
import { computed, watch } from 'vue';
import { AlertCircleIcon, CircleCheckIcon, InfoCircleIcon, HelpIcon, XIcon } from 'vue-tabler-icons';
import { useRoute } from 'vue-router';

const dialogStore = useDialogStore();
const route = useRoute();

const icon = computed(() => {
  switch (dialogStore.type) {
    case 'success':
      return CircleCheckIcon;
    case 'error':
      return AlertCircleIcon;
    case 'warning':
      return AlertCircleIcon;
    case 'question':
      return HelpIcon;
    case 'info':
    default:
      return InfoCircleIcon;
  }
});

const iconColor = computed(() => {
  switch (dialogStore.type) {
    case 'success':
      return 'success';
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'question':
      return 'primary';
    case 'info':
    default:
      return 'info';
  }
});

const hasCancelButton = computed(() => {
  return dialogStore.cancelText && dialogStore.cancelText.length > 0;
});

// 라우트 전환 중 다이얼로그가 고아 상태로 남는 경우 클릭 차단 overlay를 정리
watch(() => route.fullPath, () => {
  if (dialogStore.show) {
    dialogStore.resetDialogState();
  }
});
</script>

<template>
  <v-dialog
    v-model="dialogStore.show"
    :max-width="dialogStore.width"
    :persistent="dialogStore.persistent"
    @click:outside="dialogStore.close"
  >
    <v-card>
        <v-card-title class="pa-4 d-flex align-center" :class="`bg-${iconColor}`">
          <component :is="icon" size="24" class="mr-2 flex-shrink-0" />
          <span class="text-white dialog-title">{{ dialogStore.title }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            variant="text"
            size="small"
            @click="dialogStore.forceClose"
            class="text-white flex-shrink-0"
          >
            <XIcon size="20" />
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-6">
          <p class="text-body-1 dialog-message">{{ dialogStore.message }}</p>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            v-if="hasCancelButton"
            color="error"
            variant="flat"
            class="px-4 mr-2"
            @click="dialogStore.cancel"
          >
            {{ dialogStore.cancelText }}
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="px-4"
            @click="dialogStore.confirm"
          >
            {{ dialogStore.confirmText }}
          </v-btn>
        </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.dialog-title {
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  line-height: 1.4;
}

.dialog-message {
  white-space: pre-line;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.6;
}
</style>

